import { produce } from 'immer'
import Linkify from 'linkify-react'
import { useCallback, useEffect, useState } from 'react'
import Avatar from 'react-avatar'
import { useTranslation } from 'react-i18next'
import { Link, To, useNavigate } from 'react-router-dom'
import { useKana } from 'react-use-kana'

import {
    Alert, Button, ButtonGroup, Card, Divider, EditableText, FormGroup, H2, H4, HTMLSelect, Icon,
    InputGroup, Intent, Tab, TabId, Tabs, TabsExpander
} from '@blueprintjs/core'
import { DateInput3 } from '@blueprintjs/datetime2'
import { IconNames } from '@blueprintjs/icons'

import useCurrentUserUUID from '../../aws/useCurrentUserUUID'
import card from '../../domain/api/card'
import profile from '../../domain/api/profile'
import { GetProfileResponseEntity } from '../../domain/entities/profile'
import { useRootOutletContext } from '../root/Root'
import styles from './ProfileDetail.module.scss'
import ProfileDetailContactForms from './ProfileDetailContactForms'
import SelectProfileImage from './selectProfileImage/SelectProfileImage'

export default ({
    data: {
        id,
        cognito_uuid: profileOwnerUUID,
        data,
    },
    canEdit,
    navigatePostDeletingProfile,
}: {
    data: GetProfileResponseEntity
    canEdit?: boolean
    /** If not provided, delete button will not be shown. */
    navigatePostDeletingProfile?: (profileId: string) => To
}) => {
    const maxTabCreation = 5

    const { t, i18n } = useTranslation()
    const { toaster } = useRootOutletContext()

    // key: actual value, value: display value
    const contactUses: { [key: string]: string } = {
        "work": t('profileDetail.contactUse.work'),
        "business": t('profileDetail.contactUse.business'),
        "school": t('profileDetail.contactUse.school'),
        "shop": t('profileDetail.contactUse.shop'),
        "home": t('profileDetail.contactUse.home'),
        "personal": t('profileDetail.contactUse.personal'),
        "private": t('profileDetail.contactUse.private'),
        "emergency": t('profileDetail.contactUse.emergency'),
        "other": t('profileDetail.contactUse.other'),
    }
    const defaultContactUse = Object.keys(contactUses)[0]
    const contactUseDisplayName = (use: string) => contactUses[use] || contactUses[defaultContactUse]

    const [state, setState] = useState({
        firstName: data.first_name,
        lastName: data.last_name,
        phoneticFirstName: data.phonetic_first_name,
        phoneticLastName: data.phonetic_last_name,
        nickname: data.nickname,
        organization: data.organization,
        title: data.title,
        contacts: data.contacts || [],
        birthday: data.birthday,
        note: data.note,
        inEditMode: false,
        activeTabId: "tab-0" as TabId,
        reverseFields: false,
        isUnlinkAlertOpen: false,
        isDeleteAlertOpen: false,
        isSelectProfileImageOpen: false,
    })

    const { kana: firstNameKana, setKanaSource: setFirstNameKanaSource } = useKana({ kanaType: "katakana" })
    const { kana: lastNameKana, setKanaSource: setLastNameKanaSource } = useKana({ kanaType: "katakana" })

    const { data: profileImageDataUrl, mutate: mutateProfileImageDataUrl } = profile.image.useFetchSWR(id, {
        shouldRetryOnError: false,
        revalidateOnMount: true,
        revalidateIfStale: true,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    })
    const { data: linkedCard, isLoading: isLoadingLinkedCard } = card.useSearchSWR({ profileId: id }, {
        shouldRetryOnError: false,
    })
    const { trigger: modifyCard, isMutating: isModifyingCard } = card.useModifySWR(linkedCard?.id)
    const { trigger: modifyProfile, isMutating: isModifyingProfile } = profile.useModifySWR(id)
    const { trigger: rejectProfile, isMutating: isRejectingProfile } = profile.useRejectSWR(id)

    const navigate = useNavigate()

    const currentUserUUID = useCurrentUserUUID()
    const isProfileOwner = profileOwnerUUID === currentUserUUID
    const canEnterEditMode = isProfileOwner && canEdit === true

    // reverse the order of name and address fields for specific languages
    const reverseFields = ['ja', 'zh'].includes(i18n.resolvedLanguage ?? '')

    useEffect(() => {
        setFirstNameKanaSource(state.firstName)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.firstName])

    useEffect(() => {
        setLastNameKanaSource(state.lastName)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.lastName])

    const saveChanges = useCallback(async () => {
        const newPhoneticFirstName = state.phoneticFirstName || firstNameKana
        const newPhoneticLastName = state.phoneticLastName || lastNameKana
        setState(produce(draft => {
            draft.phoneticFirstName = newPhoneticFirstName
            draft.phoneticLastName = newPhoneticLastName
        }))

        const response = await modifyProfile({
            data: {
                first_name: state.firstName,
                last_name: state.lastName,
                phonetic_first_name: newPhoneticFirstName,
                phonetic_last_name: newPhoneticLastName,
                nickname: state.nickname,
                organization: state.organization,
                title: state.title,
                birthday: state.birthday,
                note: state.note,
                contacts: state.contacts.map(contact => ({
                    use: contact.use || defaultContactUse,
                    email: contact.email,
                    phone: contact.phone,
                    url: contact.url,
                    address: {
                        zip: contact.address.zip,
                        country: contact.address.country,
                        state: contact.address.state,
                        city: contact.address.city,
                        street: contact.address.street
                    }
                }))
            }
        })
        if (response.status !== 200)
            return toaster?.show({ message: t('toast.failedToSaveProfileChanges.message'), icon: IconNames.Error, intent: Intent.DANGER })

        toaster?.show({ message: t('toast.savedProfileChanges.message'), icon: IconNames.Tick, intent: Intent.SUCCESS })
        setState(produce(draft => {
            draft.inEditMode = false
        }))
    }, [toaster, t, modifyProfile, state, firstNameKana, lastNameKana, defaultContactUse])

    const discardChanges = useCallback(() => {
        setState(produce(draft => {
            draft.firstName = data.first_name
            draft.lastName = data.last_name
            draft.phoneticFirstName = data.phonetic_first_name
            draft.phoneticLastName = data.phonetic_last_name
            draft.nickname = data.nickname
            draft.organization = data.organization
            draft.title = data.title
            draft.contacts = data.contacts
            draft.birthday = data.birthday
            draft.note = data.note
            draft.inEditMode = false
        }))
    }, [data])

    const deleteButtonClicked = useCallback(async () => {
        linkedCard
            ? setState(produce(draft => {
                draft.isUnlinkAlertOpen = true
            }))
            : setState(produce(draft => {
                draft.isDeleteAlertOpen = true
            }))
    }, [linkedCard])

    const unlinkItemConfirmed = useCallback(async () => {
        const result = await modifyCard({ profile_id: undefined })
        result.status === 200
            ? toaster?.show({ message: t('toast.profileUnlinked.message'), icon: IconNames.Unlink, intent: Intent.SUCCESS })
            : toaster?.show({ message: t('toast.failedToUnlinkProfile.message'), icon: IconNames.Cross, intent: Intent.DANGER })
        setState(produce(draft => {
            draft.isUnlinkAlertOpen = false
        }))

        if (result.status !== 200) return
        setState(produce(draft => {
            draft.isDeleteAlertOpen = true
        }))
    }, [toaster, t, modifyCard])

    const deleteItemConfirmed = useCallback(async () => {
        const result = await rejectProfile()
        result.status === 204
            ? toaster?.show({ message: t('toast.profileDeleted.message'), icon: IconNames.Trash, intent: Intent.SUCCESS })
            : toaster?.show({ message: t('toast.failedToDeleteProfile.message'), icon: IconNames.Cross, intent: Intent.DANGER })
        setState(produce(draft => {
            draft.isDeleteAlertOpen = false
        }))

        if (result.status !== 204) return
        if (navigatePostDeletingProfile) navigate(navigatePostDeletingProfile(id))
    }, [toaster, t, rejectProfile, navigate, navigatePostDeletingProfile, id])

    const onClickAddContact = useCallback(() =>
        setState(produce(draft => {
            draft.contacts.push({
                use: defaultContactUse,
                email: "",
                phone: "",
                url: "",
                address: {
                    zip: "",
                    country: "",
                    state: "",
                    city: "",
                    street: ""
                }
            })
            draft.activeTabId = `tab-${draft.contacts.length - 1}`
        })), [defaultContactUse])

    // if data from props is updated, replace current state with the new data
    useEffect(discardChanges, [data, discardChanges])

    const editButtons = <ButtonGroup minimal>
        {state.inEditMode ? <>
            <Button
                text={t('profileDetail.saveButton.label')}
                icon={IconNames.Saved}
                intent={Intent.SUCCESS}
                onClick={saveChanges}
                disabled={isModifyingProfile} />
            <Button
                text={t('profileDetail.cancelButton.label')}
                icon={IconNames.Undo}
                intent={Intent.WARNING}
                onClick={discardChanges} />
            {navigatePostDeletingProfile &&
                <Button
                    text={t('profileDetail.deleteButton.label')}
                    icon={IconNames.Trash}
                    intent={Intent.DANGER}
                    onClick={deleteButtonClicked}
                    disabled={isLoadingLinkedCard} />}
        </> : <>
            <Button
                text={t('profileDetail.editButton.label')}
                icon={IconNames.Edit}
                intent={Intent.PRIMARY}
                onClick={() => setState(produce(draft => {
                    draft.inEditMode = true
                }))} />
        </>}
    </ButtonGroup>

    return <>
        <SelectProfileImage
            profileId={id}
            onUpload={dataUrl => {
                mutateProfileImageDataUrl(dataUrl, { revalidate: false })
                setState(produce(draft => {
                    draft.isSelectProfileImageOpen = false
                }))
            }}
            onRemove={() => {
                mutateProfileImageDataUrl(undefined, { revalidate: false })
                setState(produce(draft => {
                    draft.isSelectProfileImageOpen = false
                }))
            }}
            canRemove={!!profileImageDataUrl}
            dialogProps={{
                isOpen: state.isSelectProfileImageOpen,
                onClose: () => setState(produce(draft => {
                    draft.isSelectProfileImageOpen = false
                }))
            }}
        />

        <Alert
            className={styles.alert}
            icon={IconNames.Unlink}
            intent={Intent.DANGER}
            isOpen={state.isUnlinkAlertOpen}
            loading={isModifyingCard}
            canOutsideClickCancel
            canEscapeKeyCancel
            confirmButtonText={t('profileDetail.unlinkAlert.confirmButton.label')}
            cancelButtonText={t('profileDetail.unlinkAlert.cancelButton.label')}
            onConfirm={unlinkItemConfirmed}
            onCancel={() => setState(produce(draft => {
                draft.isUnlinkAlertOpen = false
            }))}>
            <p>{t('profileDetail.unlinkAlert.message')}</p>
        </Alert>

        <Alert
            className={styles.alert}
            icon={IconNames.Trash}
            intent={Intent.DANGER}
            isOpen={state.isDeleteAlertOpen}
            loading={isRejectingProfile}
            canOutsideClickCancel
            canEscapeKeyCancel
            confirmButtonText={t('profileDetail.deleteAlert.confirmButton.label')}
            cancelButtonText={t('profileDetail.deleteAlert.cancelButton.label')}
            onConfirm={deleteItemConfirmed}
            onCancel={() => setState(produce(draft => {
                draft.isDeleteAlertOpen = false
            }))}>
            <p>{t('profileDetail.deleteAlert.message')}</p>
        </Alert>

        <div id={styles.overviewContainer}>
            <Avatar
                round
                size="128px"
                color="#aaa"
                fgColor="#fff"
                initials={canEnterEditMode ? name => name : undefined}
                name={canEnterEditMode
                    ? t('profileDetail.profileSection.profileImage.label')
                    : t('profileDetail.profileSection.profileAvatorName', {
                        firstName: state.firstName, lastName: state.lastName
                    })}
                src={profileImageDataUrl}
                onClick={() => {
                    if (canEnterEditMode) setState(produce(draft => {
                        draft.isSelectProfileImageOpen = true
                    }))
                }} />

            <div className={`${styles.nameLabel} ${styles.horizontalContainer} ${reverseFields ? styles.reverseOrder : ""}`}>
                <H2>{state.firstName}</H2>
                <H2>{state.lastName}</H2>
            </div>
        </div>

        {canEnterEditMode && <>
            <div className={styles.editButtonsContainer}>
                {editButtons}
            </div>
        </>}
        <Card className={styles.section}>
            <H4>
                <Icon className={styles.icon} icon={IconNames.Person} />
                {t('profileDetail.profileSection.title')}
            </H4>
            <Divider className={styles.divider} />
            <div className={`${styles.horizontalContainer} ${reverseFields ? styles.reverseOrder : ""}`}>
                <FormGroup
                    className={styles.formGroup}
                    label={t('profileDetail.form.firstNameInput.label')}
                    labelFor="firstName">
                    <InputGroup
                        id="firstName"
                        className={styles.input}
                        type="text"
                        autoComplete="given-name"
                        tabIndex={reverseFields ? 12 : 11}
                        leftIcon={IconNames.User}
                        value={state.firstName}
                        onChange={e => setState(produce(draft => {
                            draft.firstName = e.target.value
                        }))}
                        readOnly={!state.inEditMode} />
                </FormGroup>
                <FormGroup
                    className={styles.formGroup}
                    label={t('profileDetail.form.lastNameInput.label')}
                    labelFor="lastName">
                    <InputGroup
                        id="lastName"
                        className={styles.input}
                        type="text"
                        autoComplete="family-name"
                        tabIndex={reverseFields ? 11 : 12}
                        leftIcon={IconNames.User}
                        value={state.lastName}
                        onChange={e => setState(produce(draft => {
                            draft.lastName = e.target.value
                        }))}
                        readOnly={!state.inEditMode} />
                </FormGroup>
            </div>
            <div className={`${styles.horizontalContainer} ${reverseFields ? styles.reverseOrder : ""}`}>
                <FormGroup
                    className={styles.formGroup}
                    label={t('profileDetail.form.phoneticFirstNameInput.label')}
                    labelFor="phoneticFirstName">
                    <InputGroup
                        id="phoneticFirstName"
                        className={styles.input}
                        type="text"
                        autoComplete="off"
                        tabIndex={reverseFields ? 14 : 13}
                        leftIcon={IconNames.Translate}
                        placeholder={firstNameKana}
                        value={state.phoneticFirstName}
                        onChange={e => setState(produce(draft => {
                            draft.phoneticFirstName = e.target.value
                        }))}
                        readOnly={!state.inEditMode} />
                </FormGroup>
                <FormGroup
                    className={styles.formGroup}
                    label={t('profileDetail.form.phoneticLastNameInput.label')}
                    labelFor="phoneticLastName">
                    <InputGroup
                        id="phoneticLastName"
                        className={styles.input}
                        type="text"
                        autoComplete="off"
                        tabIndex={reverseFields ? 13 : 14}
                        leftIcon={IconNames.Translate}
                        placeholder={lastNameKana}
                        value={state.phoneticLastName}
                        onChange={e => setState(produce(draft => {
                            draft.phoneticLastName = e.target.value
                        }))}
                        readOnly={!state.inEditMode} />
                </FormGroup>
            </div>
            <FormGroup
                className={styles.formGroup}
                label={t('profileDetail.form.nicknameInput.label')}
                labelFor="nickname">
                <InputGroup
                    id="nickname"
                    className={styles.input}
                    type="text"
                    autoComplete="nickname"
                    tabIndex={15}
                    leftIcon={IconNames.Chat}
                    value={state.nickname}
                    onChange={e => setState(produce(draft => {
                        draft.nickname = e.target.value
                    }))}
                    readOnly={!state.inEditMode} />
            </FormGroup>
            <div className={styles.horizontalContainer}>
                <FormGroup
                    className={styles.formGroup}
                    label={t('profileDetail.form.organizationInput.label')}
                    labelFor="organization">
                    <InputGroup
                        id="organization"
                        className={styles.input}
                        type="text"
                        autoComplete="organization"
                        tabIndex={16}
                        leftIcon={IconNames.Office}
                        value={state.organization}
                        onChange={e => setState(produce(draft => {
                            draft.organization = e.target.value
                        }))}
                        readOnly={!state.inEditMode} />
                </FormGroup>
                <FormGroup
                    className={styles.formGroup}
                    label={t('profileDetail.form.titleInput.label')}
                    labelFor="title">
                    <InputGroup
                        id="title"
                        className={styles.input}
                        type="text"
                        autoComplete="organization-title"
                        tabIndex={17}
                        leftIcon={IconNames.Briefcase}
                        value={state.title}
                        onChange={e => setState(produce(draft => {
                            draft.title = e.target.value
                        }))}
                        readOnly={!state.inEditMode} />
                </FormGroup>
            </div>
            <FormGroup
                className={styles.formGroup}
                label={t('profileDetail.form.birthdayInput.label')}
                labelFor="birthday">
                {state.inEditMode ? <>
                    <DateInput3
                        inputProps={{
                            id: "birthday",
                            leftIcon: IconNames.Calendar,
                            tabIndex: 18,
                            autoComplete: "bday",
                        }}
                        className={styles.input}
                        dateFnsFormat="yyyy-MM-dd"
                        maxDate={new Date()}
                        minDate={new Date(1900, 0, 1)}
                        value={state.birthday}
                        onChange={date => setState(produce(draft => {
                            draft.birthday = date || ''
                        }))}
                        closeOnSelection
                        locale={i18n.resolvedLanguage} />
                </> : <>
                    {/* to avoid the warning: Incorrect use of labelFor and input id pair. */}
                    <input id="birthday" className={styles.dummyInput} />
                    <p id="birthday" className={styles.linkGroup}>
                        <Icon className={styles.icon} icon={IconNames.Calendar} />
                        {state.birthday}
                    </p>
                </>}
            </FormGroup>
            <FormGroup
                className={styles.formGroup}
                label={t('profileDetail.form.noteInput.label')}
                labelFor="note">
                {/* to avoid the warning: Incorrect use of labelFor and input id pair. */}
                <input id="note" className={styles.dummyInput} />
                {state.inEditMode ? <>
                    <EditableText
                        className={styles.input}
                        type="text"
                        placeholder={t('profileDetail.form.noteInput.placeholder')}
                        multiline
                        value={state.note}
                        onChange={e => setState(produce(draft => {
                            draft.note = e
                        }))} />
                </> : <>
                    <Linkify
                        as={"span"}
                        options={{
                            render: ({ attributes, content }) => {
                                const { href, ...props } = attributes
                                const target = href?.startsWith("http") ? "_blank" : undefined
                                const rel = target ? "noopener noreferrer" : undefined
                                return <Link {...props} to={href} target={target} rel={rel}>
                                    {content}
                                </Link>
                            }
                        }}>
                        {state.note || ' '}
                    </Linkify>
                </>}
            </FormGroup>
        </Card>

        {canEnterEditMode && <>
            <div className={styles.editButtonsContainer}>
                {editButtons}
            </div>
        </>}
        <Card className={styles.section}>
            <H4>
                <Icon className={styles.icon} icon={IconNames.GlobeNetwork} />
                {t('profileDetail.contactSection.title')}
            </H4>
            <Tabs
                onChange={tabId => setState(produce(draft => {
                    draft.activeTabId = tabId
                }))}
                selectedTabId={state.activeTabId}
                className={styles.contactTabs}>
                {state.contacts.map((contact, index) =>
                    <Tab
                        className={styles.contactTab}
                        key={`tab-${index}`}
                        id={`tab-${index}`}
                        title={state.inEditMode && state.activeTabId === `tab-${index}` ?
                            <HTMLSelect
                                id={`use-${index}`}
                                value={contact.use}
                                options={[
                                    ...Object.entries(contactUses).map(([key, value]) => ({ label: value, value: key })),
                                    ({ label: t('profileDetail.contactUse.delete'), value: "" })
                                ]}
                                onChange={e => setState(produce(draft => {
                                    if (e.target.value) draft.contacts[index].use = e.target.value
                                    else {
                                        draft.contacts.splice(index, 1)
                                        if (index > 0 && index === state.contacts.length - 1) setState(produce(draft => {
                                            draft.activeTabId = `tab-${index - 1}`
                                        }))
                                    }
                                }))}
                                minimal /> : contactUseDisplayName(contact.use)}
                        panel={
                            <ProfileDetailContactForms
                                idSuffix={index}
                                inEditMode={state.inEditMode}
                                reverseFields={reverseFields}
                                contact={contact}
                                setContact={payload =>
                                    typeof payload === "function"
                                        ? setState(produce(draft => {
                                            draft.contacts[index] = payload(draft.contacts[index])
                                        }))
                                        : setState(produce(draft => {
                                            draft.contacts[index] = payload
                                        }))
                                }
                            />}
                    />)}

                {state.inEditMode && <>
                    <TabsExpander />
                    <Button
                        className={styles.addContactUseButton}
                        text={t('profileDetail.form.addContactUseButton.label')}
                        icon={IconNames.Add}
                        onClick={onClickAddContact}
                        disabled={state.contacts.length >= maxTabCreation} />
                </>}
            </Tabs>
        </Card>
    </>
}
